import { ObjectListTableResizable } from "@/components/layout-2/object-list-table-resizable-2";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { SearchType } from "@/types/crm/search";
import { useDebounce } from "@/utils/misc";
import { looksLikeSalesforceId } from "@/utils/salesforce-id";
import { generateAutoColumn } from "@/utils/object-list-generators";
import { chunkSearchTypes, fetchSearchMulti, SEARCH_TIMEOUT_MESSAGE } from "@/utils/search-light";
import * as LucideIcons from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Remplacer SearchResultItem par un type dynamique pour les résultats de recherche
type DynamicSearchResult = { [key: string]: any };

type PaginatedSearchResponse = {
    results: {
        columns?: string[];
        results: DynamicSearchResult[];
    };
};

const SEARCH_PAGE_SIZE = 10;

interface SearchTypeState {
    loading: boolean;
    error: string | null;
    results: DynamicSearchResult[];
    hasMore: boolean;
    page: number;
    icon?: string;
    color?: string;
    model_name?: string;
    model_name_plural?: string;
    columns?: string[];
    priority: number;
}

export function SearchPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const query = searchParams.get("q") || "";
    const debouncedQuery = useDebounce(query, 400);
    const urlType = searchParams.get("type") || "all";
    const [typesMeta, setTypesMeta] = useState<SearchType[]>([]); // [{type, icon, color, ...}]
    const [searchByType, setSearchByType] = useState<Record<string, SearchTypeState>>({});
    const [selectedType, setSelectedType] = useState<string>(urlType);

    // If the user landed on /search?q=<sf_id>, bounce to the technical redirect URL.
    // Skip when nofollow=1 (set by /sf/:sfId after a failed resolve) to avoid a loop.
    useEffect(() => {
        const trimmed = query.trim();
        if (searchParams.get("nofollow") === "1") {
            return;
        }
        if (looksLikeSalesforceId(trimmed)) {
            navigate(`/sf/${encodeURIComponent(trimmed)}`, { replace: true });
        }
    }, [query, navigate, searchParams]);

    // Synchronise selectedType avec l'URL si elle change
    useEffect(() => {
        setSelectedType(urlType);
    }, [urlType]);

    // Fetch la liste des types au mount
    useEffect(() => {
        DataService.client.url("/crm/app/search/types/").get().json<SearchType[]>().then(setTypesMeta);
    }, []);

    useEffect(() => {
        document.title = `${query} - Search | Walter CRM`;
    }, [query]);

    // Fetch asynchrone par type à chaque changement de query
    useEffect(() => {
        setSearchByType({});
        setSelectedType("all");
        if (!debouncedQuery || typesMeta.length === 0) {
            return;
        }
        const controller = new AbortController();
        const enabledTypes = typesMeta.filter(typeMeta => typeMeta.is_global_search_enabled);
        const newState: Record<string, SearchTypeState> = {};
        enabledTypes.forEach(typeMeta => {
            newState[typeMeta.type] = {
                loading: true,
                error: null,
                results: [],
                hasMore: false,
                page: 1,
                icon: typeMeta.icon,
                color: typeMeta.color,
                model_name: typeMeta.model_name,
                model_name_plural: typeMeta.model_name_plural,
                columns: [],
                priority: typeMeta.priority
            };
        });
        setSearchByType(newState);
        void Promise.all(
            chunkSearchTypes(enabledTypes).map(async chunk => {
                try {
                    const { results, errors } = await fetchSearchMulti<DynamicSearchResult>(
                        debouncedQuery,
                        chunk.map(typeMeta => typeMeta.type),
                        "global",
                        controller
                    );
                    if (controller.signal.aborted) return;
                    setSearchByType(prev => {
                        const next = { ...prev };
                        for (const typeMeta of chunk) {
                            const typeResults = results[typeMeta.type]?.results || [];
                            next[typeMeta.type] = {
                                ...prev[typeMeta.type],
                                loading: false,
                                error: errors[typeMeta.type] ? SEARCH_TIMEOUT_MESSAGE : null,
                                results: typeResults,
                                // ponytail: A full page may mean there are exactly 10 results, causing one harmless
                                // extra request. Replace this heuristic when the API exposes a has_more flag.
                                hasMore: typeResults.length === SEARCH_PAGE_SIZE,
                                page: 1,
                                columns: results[typeMeta.type]?.columns || []
                            };
                        }
                        return next;
                    });
                } catch (err) {
                    if (controller.signal.aborted) return;
                    setSearchByType(prev => {
                        const next = { ...prev };
                        for (const typeMeta of chunk) {
                            next[typeMeta.type] = {
                                ...prev[typeMeta.type],
                                loading: false,
                                error: err instanceof Error ? err.message : "Error while searching"
                            };
                        }
                        return next;
                    });
                }
            })
        );
        return () => controller.abort();
    }, [debouncedQuery, typesMeta]);

    // Handler pour "Voir plus"
    const handleSeeMore = (type: string) => {
        const nextPage = (searchByType[type]?.page || 1) + 1;
        setSearchByType(prev => ({
            ...prev,
            [type]: { ...prev[type], loading: true }
        }));
        DataService.client
            .url("/crm/app/search/paginate/")
            .query({ q: query, type, page: nextPage })
            .get()
            .json<PaginatedSearchResponse>()
            .then(data => {
                const results = data.results.results;
                setSearchByType(prev => {
                    const updated = {
                        ...prev,
                        [type]: {
                            ...prev[type],
                            loading: false,
                            results: [...(prev[type]?.results || []), ...results],
                            hasMore: results.length === SEARCH_PAGE_SIZE,
                            page: nextPage,
                            columns: data.results.columns || prev[type].columns
                        }
                    };
                    return { ...updated };
                });
            })
            .catch(err => {
                setSearchByType(prev => {
                    const updated = {
                        ...prev,
                        [type]: {
                            ...prev[type],
                            loading: false,
                            error: err.message || "Erreur lors de la recherche"
                        }
                    };
                    return { ...updated };
                });
            });
    };

    const types = typesMeta.map(t => t.type);
    const allResultsCount = types.reduce((acc, type) => acc + (searchByType[type]?.results.length || 0), 0);

    // Helper pour savoir si tous les types sont chargés
    const allLoaded = types.every(type => !searchByType[type]?.loading);
    // Nouveau : savoir si au moins un type est en cours de chargement
    const isSearching = types.some(type => searchByType[type]?.loading);

    if (!query) {
        return (
            <div className="container mx-auto py-8">
                <div className="text-center">
                    <LucideIcons.ArrowUp className="mx-auto mb-4 h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
                    <p className="mb-6 text-muted-foreground">Utilisez la barre de recherche pour trouver des contacts, comptes, opportunités et plus encore</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-4 grid grid-cols-12 gap-5">
            <div className="sticky top-4 col-span-2 h-[calc(100vh-200px)] space-y-1 overflow-y-auto rounded-md bg-white p-4  shadow-md">
                <p className="mb-4 ml-2 text-sm font-medium">Filter by object type</p>

                {/* Option "Tous les résultats" */}
                <div
                    className={cn("relative flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-gray-100", selectedType === "all" && " bg-gray-100")}
                    onClick={() => {
                        setSelectedType("all");
                        setSearchParams(params => {
                            params.set("q", query);
                            params.delete("type");
                            return params;
                        });
                    }}
                >
                    <div className="flex items-center space-x-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded bg-gray-200">
                            <LucideIcons.Search className="h-4 w-4 text-black" />
                        </div>
                        <p className="text-sm">All results</p>
                    </div>
                    <div className="flex h-6 w-6 items-center justify-center rounded bg-gray-200 text-xs text-black">{allResultsCount}</div>
                </div>

                {/* Options par type */}
                {typesMeta
                    .filter(typeMeta => {
                        const group = searchByType[typeMeta.type];
                        return (group && group.results.length !== 0 && !group.loading) || (group && group.results.length > 0 && group.loading);
                    })
                    .sort((a, b) => (a.priority || 100) - (b.priority || 100))
                    .map(typeMeta => {
                        const type = typeMeta.type;
                        const IconComponent = typeMeta.icon && (LucideIcons as any)[typeMeta.icon] ? (LucideIcons as any)[typeMeta.icon] : LucideIcons["Search"];
                        return (
                            <div
                                key={type}
                                className={cn("relative flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-gray-100", selectedType === type && " bg-gray-100")}
                                onClick={() => {
                                    setSelectedType(type);
                                    setSearchParams(params => {
                                        params.set("q", query);
                                        params.set("type", type);
                                        return params;
                                    });
                                }}
                            >
                                <div className="flex items-center space-x-2">
                                    <span className={cn("flex h-6 w-6 items-center justify-center rounded")} style={{ backgroundColor: typeMeta.color }}>
                                        {IconComponent && <IconComponent className="h-4 w-4 text-white" />}
                                    </span>
                                    <p className="text-sm">{typeMeta.model_name_plural || type}</p>
                                </div>
                                <div className={cn("flex h-6 w-6 items-center justify-center rounded bg-gray-200 text-xs text-black")}>
                                    {searchByType[type]?.results.length || 0}
                                    {searchByType[type]?.hasMore ? "+" : ""}
                                </div>
                            </div>
                        );
                    })}
            </div>

            {/* Contenu principal */}
            <div className="col-span-10 h-[calc(100vh-160px)] space-y-6 overflow-y-auto pb-10 pl-2">
                {/* Header de recherche */}
                <div className="flex flex-col justify-between">
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold">Results for "{query}"</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <p className="text-muted-foreground">
                            {selectedType === "all"
                                ? `${allResultsCount} result${allResultsCount > 1 ? "s" : ""} found`
                                : `${searchByType[selectedType]?.results.length || 0} result${(searchByType[selectedType]?.results.length || 0) > 1 ? "s" : ""} found in ${typesMeta.find(t => t.type === selectedType)?.model_name_plural || selectedType}`}
                        </p>
                        {isSearching ? (
                            <span className="inline-flex w-fit items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                <LucideIcons.Loader className="mr-1 h-3 w-3 animate-spin" />
                                Searching...
                            </span>
                        ) : (
                            <span className="inline-flex w-fit items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                <LucideIcons.CheckCircle className="mr-1 h-3 w-3" />
                                Search completed
                            </span>
                        )}
                    </div>
                </div>

                {/* Tableaux des résultats groupés par type */}
                {(selectedType === "all" ? types : [selectedType])
                    .filter(type => {
                        const group = searchByType[type];
                        // Affiche si en cours de chargement, en erreur OU s'il y a des résultats
                        return group && (group.loading || group.error || group.results.length > 0);
                    })
                    .sort((a, b) => (searchByType[a].priority || 100) - (searchByType[b].priority || 100))
                    .map(type => {
                        const group = searchByType[type];
                        const typeMeta = typesMeta.find(t => t.type === type);
                        if (!group || !typeMeta) return null;
                        const IconComponent = typeMeta.icon && (LucideIcons as any)[typeMeta.icon] ? (LucideIcons as any)[typeMeta.icon] : LucideIcons["Search"];
                        const columns = group.columns || [];
                        const paginated_response = {
                            count: group.results.length,
                            results: group.results
                        };
                        // Colonnes dynamiques pour le tableau
                        const dynamicColumns = columns.map(col => {
                            const baseCol = generateAutoColumn<DynamicSearchResult>(col, null);
                            return {
                                ...baseCol,
                                name: col.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
                                accessorKey: col
                            };
                        });

                        return (
                            <div key={type} className="flex w-full flex-col items-start rounded-md  bg-white pt-4 shadow-md">
                                <div className="flex w-full flex-col">
                                    <div className="mb-4 ml-4 flex items-center space-x-2">
                                        <span className={cn("flex h-6 w-6 items-center justify-center rounded")} style={{ backgroundColor: typeMeta.color }}>
                                            {IconComponent && <IconComponent className="h-4 w-4 text-white" />}
                                        </span>
                                        <span className=" font-bold">
                                            {typeMeta.model_name_plural || type} ({group.results.length}
                                            {group.hasMore ? "+" : ""})
                                        </span>
                                    </div>
                                    {group.results.length === 0 && group.loading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <LucideIcons.Loader className="h-6 w-6 animate-spin text-gray-500" />
                                            <span className="ml-2 text-muted-foreground">Loading...</span>
                                        </div>
                                    ) : group.error ? (
                                        <Card className="p-8">
                                            <div className="text-center">
                                                <LucideIcons.AlertCircle className="mx-auto mb-4 h-10 w-10 text-red-500" strokeWidth={1.5} />
                                                <p className="text-muted-foreground">{group.error}</p>
                                            </div>
                                        </Card>
                                    ) : (
                                        <div className="space-y-2 pb-2">
                                            <ObjectListTableResizable<DynamicSearchResult>
                                                columns={dynamicColumns}
                                                paginated_response={paginated_response}
                                                allowAutoRedirect={false}
                                                allowBatchSelection={false}
                                                allowColumnResizing={false}
                                                allowColumnOrdering={false}
                                                layout="dense"
                                                itemToId={item => item.id?.toString() || ""}
                                                selectedItems={[]}
                                                onRowNavigateTo={item => item.href}
                                                clickable_field={columns[0]}
                                            />
                                            {group.hasMore && (
                                                <div className="flex items-center justify-center gap-2 ">
                                                    <button
                                                        className="rounded bg-gray-100 px-4 py-1 text-sm text-primary hover:bg-gray-200 disabled:opacity-50"
                                                        onClick={() => handleSeeMore(type)}
                                                        disabled={group.loading}
                                                    >
                                                        See more
                                                    </button>
                                                    {group.loading && group.results.length > 0 && <LucideIcons.Loader className="h-4 w-4 animate-spin text-gray-500" />}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                {/* Message si aucun résultat */}
                {allLoaded && allResultsCount === 0 && (
                    <Card className="p-8">
                        <div className="text-center">
                            <LucideIcons.Search className="mx-auto mb-4 h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
                            <p className="text-muted-foreground">
                                No results found for "{query}"{selectedType !== "all" ? ` in ${typesMeta.find(t => t.type === selectedType)?.model_name_plural || selectedType}` : ""}
                            </p>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}
